import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Badge, Banner, Button, Card, Field, PageBody, PageHeader, Textarea, Well } from "../ui/Field";

type ThemeTokens = {
  colors?: Record<string, string>;
  fonts?: { sans?: { family: string; weights?: number[]; subsets?: string[] } };
  spacing?: number[];
  radii?: number[];
  shadows?: string[];
};

export default function Theme() {
  const { slug } = useParams();
  const [tokens, setTokens] = useState<ThemeTokens | null>(null);
  const [version, setVersion] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [raw, setRaw] = useState("");
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    api.get<{ version: number; tokens: ThemeTokens }>(`/api/sites/${slug}/theme`)
      .then((r) => { setVersion(r.version); setTokens(r.tokens); setRaw(JSON.stringify(r.tokens, null, 2)); })
      .catch((e) => setErr((e as Error).message));
  }, [slug]);

  async function save(payload: ThemeTokens) {
    setErr(null); setOk(null);
    try {
      const r = await api.put<{ version: number }>(`/api/sites/${slug}/theme`, payload);
      setVersion(r.version);
      setTokens(payload);
      setRaw(JSON.stringify(payload, null, 2));
      setOk("Saved.");
      setTimeout(() => setOk(null), 2000);
    } catch (e) { setErr((e as Error).message); }
  }

  function updateColor(key: string, val: string) {
    if (!tokens) return;
    const next = { ...tokens, colors: { ...tokens.colors, [key]: val } };
    setTokens(next);
  }

  function commitColors() { if (tokens) save(tokens); }

  function saveRaw() {
    try {
      const parsed = JSON.parse(raw);
      save(parsed);
    } catch (e) {
      setErr("Invalid JSON: " + (e as Error).message);
    }
  }

  if (!tokens) return <PageBody><div className="h-32 bg-[var(--color-paper-2)] animate-pulse rounded" /></PageBody>;

  const colors = Object.entries(tokens.colors ?? {});
  const font = tokens.fonts?.sans?.family ?? "Inter";

  return (
    <PageBody>
      <PageHeader
        eyebrow="№ 07 · Theme"
        title={<>Visual <em>tokens</em></>}
        description="Tokens drive every block at build time. Changes apply to all pages on the next publish."
        actions={<Badge tone="neutral">v{version}</Badge>}
      />
      {err && <Banner kind="error">{err}</Banner>}
      {ok && <Banner kind="success">{ok}</Banner>}

      <Card number="T1" title="Palette" description="Click any swatch to edit. Save commits a new version.">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {colors.map(([k, v]) => (
            <label key={k} className="group block">
              <div className="aspect-[3/1] rounded-[var(--radius-sm)] border border-[var(--color-rule)] overflow-hidden relative">
                <div className="absolute inset-0" style={{ background: v }} />
                <input
                  type="color"
                  value={v}
                  onChange={(e) => updateColor(k, e.target.value)}
                  onBlur={commitColors}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </div>
              <div className="flex items-center justify-between gap-2 mt-1.5">
                <span className="text-[12px] text-[var(--color-ink)] font-medium truncate">{k}</span>
                <code className="font-mono text-[10.5px] text-[var(--color-muted)]">{v}</code>
              </div>
            </label>
          ))}
        </div>
      </Card>

      <Card number="T2" title="Typography" className="mt-6">
        <Field
          label="Display family"
          value={font}
          onChange={(e) => setTokens({ ...tokens, fonts: { sans: { ...(tokens.fonts?.sans ?? { weights: [400, 500, 600], subsets: ["latin"] }), family: e.target.value } } })}
          onBlur={() => save(tokens)}
          help="Any Google Fonts family. Loaded at build time."
        />
        <Well>
          <div className="text-[34px] leading-tight" style={{ fontFamily: `'${font}', system-ui, sans-serif` }}>
            The quick brown fox jumps over the lazy dog.
          </div>
          <div className="text-[14px] mt-2 text-[var(--color-muted)]" style={{ fontFamily: `'${font}', system-ui, sans-serif` }}>
            Preview at runtime — actual pages use this family.
          </div>
        </Well>
      </Card>

      <Card
        number="T3"
        title="Raw JSON"
        description="Full token tree — spacing, radii, shadows."
        actions={<Button size="sm" variant="ghost" onClick={() => setShowRaw((s) => !s)}>{showRaw ? "Hide" : "Show"}</Button>}
        className="mt-6"
      >
        {showRaw && (
          <>
            <Textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={20} className="mb-3" />
            <Button onClick={saveRaw}>Save JSON</Button>
          </>
        )}
      </Card>
    </PageBody>
  );
}
